/**
 * Free E.V.I.L. Scan public terminal.
 *
 * Security model:
 * - Tokens live only in sessionStorage and Authorization headers.
 * - Verification material (?fsv=) is stripped from the visible URL immediately.
 * - No tokens or PII are placed in DOM attributes, analytics, or logs.
 * - Private result states add a noindex robots meta as defense in depth.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fes-root]');
  if (!root) return;

  var API_BASE = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
  var TURNSTILE_SITEKEY = root.getAttribute('data-turnstile-sitekey') || '';
  var STORAGE_STATUS = 'fes_status_token';
  var STORAGE_ACCESS = 'fes_access_token';
  var POLL_MS = 4000;

  var els = {
    live: root.querySelector('[data-fes-live]'),
    panels: {
      form: root.querySelector('[data-fes-panel="form"]'),
      progress: root.querySelector('[data-fes-panel="progress"]'),
      report: root.querySelector('[data-fes-panel="report"]'),
      notice: root.querySelector('[data-fes-panel="notice"]')
    },
    form: root.querySelector('[data-fes-form]'),
    errorSummary: root.querySelector('[data-fes-error-summary]'),
    errorList: root.querySelector('[data-fes-error-list]'),
    submit: root.querySelector('[data-fes-submit]'),
    turnstile: root.querySelector('[data-fes-turnstile]'),
    turnstileLoading: root.querySelector('[data-fes-turnstile-loading]'),
    turnstileError: root.querySelector('[data-fes-turnstile-error]'),
    statusPill: root.querySelector('[data-fes-status-pill]'),
    ref: root.querySelector('[data-fes-ref]'),
    progressTitle: root.querySelector('[data-fes-progress-title]'),
    progressDetail: root.querySelector('[data-fes-progress-detail]'),
    progressActions: root.querySelector('[data-fes-progress-actions]'),
    resend: root.querySelector('[data-fes-resend]'),
    resendStatus: root.querySelector('[data-fes-resend-status]'),
    steps: root.querySelector('[data-fes-steps]'),
    reportPill: root.querySelector('[data-fes-report-pill]'),
    reportRef: root.querySelector('[data-fes-report-ref]'),
    reportTitle: root.querySelector('[data-fes-report-title]'),
    reportBody: root.querySelector('[data-fes-report-body]'),
    cta: root.querySelector('[data-fes-cta]'),
    ctaLink: root.querySelector('[data-fes-cta-link]'),
    noticeTitle: root.querySelector('[data-fes-notice-title]'),
    noticeBody: root.querySelector('[data-fes-notice-body]'),
    noticeCta: root.querySelector('[data-fes-notice-cta]'),
    noticeResend: root.querySelector('[data-fes-notice-resend]'),
    noticeResendStatus: root.querySelector('[data-fes-notice-resend-status]'),
    startOver: root.querySelector('[data-fes-start-over]')
  };

  var turnstileWidgetId = null;
  var pollTimer = null;
  var ctaRecorded = false;

  // Authored in the section so the copy stays in one place.
  var TURNSTILE_DEFAULT_ERROR = els.turnstileError
    ? els.turnstileError.textContent.replace(/\s+/g, ' ').trim()
    : '';

  /* ---------------- utilities ---------------- */

  function announce(message) {
    if (els.live) els.live.textContent = message;
  }

  function addNoindex() {
    if (document.querySelector('meta[name="robots"][data-fes]')) return;
    var meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    meta.setAttribute('data-fes', '');
    document.head.appendChild(meta);
  }

  function getToken(key) {
    try { return window.sessionStorage.getItem(key) || ''; } catch (e) { return ''; }
  }
  function setToken(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function clearTokens() {
    try {
      window.sessionStorage.removeItem(STORAGE_STATUS);
      window.sessionStorage.removeItem(STORAGE_ACCESS);
    } catch (e) { /* noop */ }
  }

  function api(path, options) {
    options = options || {};
    var headers = { 'Content-Type': 'application/json' };
    if (options.token) headers.Authorization = 'Bearer ' + options.token;
    return fetch(API_BASE + path, {
      method: options.method || 'GET',
      headers: headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
      credentials: 'omit',
      referrerPolicy: 'no-referrer'
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        return { status: response.status, data: data };
      });
    });
  }

  function showPanel(name) {
    Object.keys(els.panels).forEach(function (key) {
      var panel = els.panels[key];
      if (panel) panel.hidden = key !== name;
    });
  }

  function setPill(element, text, tone) {
    if (!element) return;
    element.textContent = text;
    element.className = 'fes__status-pill' +
      (tone === 'warn' ? ' fes__status-pill--warn' : '') +
      (tone === 'critical' ? ' fes__status-pill--critical' : '');
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function stopPolling() {
    if (pollTimer) { window.clearTimeout(pollTimer); pollTimer = null; }
  }

  /* Visible status text beside a resend button. announce() covers the screen-reader side. */
  function setStatus(element, message, isError) {
    if (!element) return;
    element.textContent = message || '';
    element.hidden = !message;
    element.className = message && isError ? 'fes__field-error' : 'fes__field-hint';
  }

  function showTurnstileError(message) {
    if (!els.turnstileError) return;
    els.turnstileError.textContent = message || TURNSTILE_DEFAULT_ERROR;
    els.turnstileError.hidden = false;
  }

  function clearTurnstileError() {
    if (els.turnstileError) els.turnstileError.hidden = true;
  }

  /* ---------------- notice states ---------------- */

  function showNotice(title, paragraphs, options) {
    options = options || {};
    stopPolling();
    els.noticeTitle.textContent = title;
    els.noticeBody.textContent = '';
    paragraphs.forEach(function (text) {
      els.noticeBody.appendChild(el('p', null, text));
    });
    els.startOver.hidden = !options.startOver;
    els.noticeCta.hidden = !options.cta;
    if (els.noticeResend) els.noticeResend.hidden = !options.resend;
    setStatus(els.noticeResendStatus, '', false);
    showPanel('notice');
    announce(title);
    els.noticeTitle.setAttribute('tabindex', '-1');
    els.noticeTitle.focus({ preventScroll: false });
  }

  function showRecentlyScanned() {
    showNotice(
      'This website was scanned recently',
      [
        'A Free E.V.I.L. Scan for this website is already in progress or was completed within ' +
        'the last 30 days, so a new automated scan is not available yet.',
        'A Schema Health Review is available at any time and goes far deeper than this ' +
        'automated homepage check.'
      ],
      { cta: true, startOver: true }
    );
  }

  function showVerificationExpired(canResend) {
    // Only offer the resend button when the status token that the endpoint needs is still held,
    // so the copy and the available action never disagree.
    var resendReady = Boolean(canResend) && Boolean(getToken(STORAGE_STATUS));
    showNotice(
      'Verification link expired',
      resendReady
        ? ['Your verification link has expired. Request a new email below, or start over.']
        : [
            'Your verification link has expired or was already used. If you still want the scan, ' +
            'please submit the form again from this page.'
          ],
      { startOver: true, resend: resendReady }
    );
  }

  function showResultExpired() {
    clearTokens();
    showNotice(
      'This result link has expired',
      [
        'Free E.V.I.L. Scan results are available for a limited time and this link is no longer ' +
        'valid. You can request a new scan after the 30-day window, or move straight to a ' +
        'human-review next step.'
      ],
      { cta: true, startOver: true }
    );
  }

  function showFailed(retryUnavailable) {
    showNotice(
      retryUnavailable ? 'Scan unavailable for this website' : 'The scan could not complete',
      retryUnavailable
        ? [
            'The automated scan retried and could not complete for this website. This does not ' +
            'mean anything is wrong with your site — many sites cannot be read by a restricted ' +
            'automated fetch.',
            'A human-review next step does not depend on this automated scan.'
          ]
        : [
            'The automated scan hit a problem and will retry automatically. Check back in a few ' +
            'minutes using the same link.'
          ],
      { cta: retryUnavailable, startOver: retryUnavailable }
    );
  }

  /* ---------------- progress states ---------------- */

  var PROGRESS_COPY = {
    submitting: ['Submitting your request…', 'Checking your details and running the security check.'],
    eligibility: ['Checking eligibility…', 'Confirming this website can be scanned safely.'],
    awaiting_email_verification: [
      'Check your email',
      'We sent a one-time verification link to your business email. The scan starts only after ' +
      'you confirm. The link expires quickly — check spam if you do not see it.'
    ],
    verifying: ['Confirming your email…', 'Validating your one-time verification link.'],
    verified: ['Email confirmed', 'Your scan is being queued now.'],
    queued: ['Scan queued', 'Your homepage scan is in line and will start shortly.'],
    fetching: ['Fetching your homepage…', 'Reading only your public homepage. No logins, no cookies.'],
    scanning: ['Scanning structured data…', 'The scanner is evaluating what it can read.'],
    filtering: ['Preparing your report…', 'Building the public-safe report.']
  };

  function showProgress(stateKey, publicRef) {
    var copy = PROGRESS_COPY[stateKey] || PROGRESS_COPY.queued;
    els.progressTitle.textContent = copy[0];
    els.progressDetail.textContent = copy[1];
    if (publicRef) els.ref.textContent = 'Ref ' + publicRef;
    var tone = stateKey === 'awaiting_email_verification' ? 'warn' : '';
    setPill(els.statusPill, copy[0], tone);
    var order = ['queued', 'fetching', 'scanning', 'filtering'];
    var activeIndex = order.indexOf(stateKey);
    if (els.steps) {
      Array.prototype.forEach.call(els.steps.children, function (item) {
        var index = order.indexOf(item.getAttribute('data-step'));
        item.removeAttribute('data-active');
        item.removeAttribute('data-done');
        if (activeIndex > -1) {
          if (index === activeIndex) item.setAttribute('data-active', '');
          if (index > -1 && index < activeIndex) item.setAttribute('data-done', '');
        }
      });
      els.steps.style.display = activeIndex > -1 ? '' : 'none';
    }
    els.progressActions.hidden = stateKey !== 'awaiting_email_verification';
    showPanel('progress');
    announce(copy[0] + ' ' + copy[1]);
  }

  /* ---------------- report rendering ---------------- */

  var STATUS_LABELS = {
    completed: ['Scan complete', ''],
    completed_with_limitations: ['Complete with limitations', 'warn'],
    human_review_recommended: ['Human review recommended', 'warn'],
    unable_to_scan: ['Unable to scan', 'critical'],
    recently_scanned: ['Recently scanned', 'warn']
  };

  function renderList(title, items, options) {
    options = options || {};
    if (!items || !items.length) return null;
    var block = el('section', 'fes__report-block' + (options.dim ? ' fes__report-block--dim' : '') + (options.warn ? ' fes__report-block--warn' : ''));
    block.appendChild(el('h4', null, title));
    var list = el('ul');
    items.forEach(function (item) {
      list.appendChild(el('li', null, String(item)));
    });
    block.appendChild(list);
    return block;
  }

  function renderReport(payload) {
    stopPolling();
    addNoindex();
    var report = payload.report || {};
    var identity = report.identity || {};
    var statusInfo = STATUS_LABELS[report.status] || STATUS_LABELS.completed;
    setPill(els.reportPill, statusInfo[0], statusInfo[1]);
    els.reportRef.textContent = identity.publicScanReference ? 'Ref ' + identity.publicScanReference : '';
    els.reportBody.textContent = '';

    var idBlock = el('section', 'fes__report-block');
    idBlock.appendChild(el('h4', null, identity.submittedBusinessName || 'Your business'));
    if (identity.normalizedWebsiteDisplay) {
      idBlock.appendChild(el('p', null, 'Website scanned: ' + identity.normalizedWebsiteDisplay));
    }
    if (identity.scanScopeSummary) idBlock.appendChild(el('p', null, identity.scanScopeSummary));
    if (identity.scanCompletionDate) {
      var when = new Date(identity.scanCompletionDate);
      if (!isNaN(when.getTime())) {
        idBlock.appendChild(el('p', null, 'Completed: ' + when.toLocaleString()));
      }
    }
    els.reportBody.appendChild(idBlock);

    var observations = (report.safeObservations || []).map(function (item) {
      return item && item.summary ? item.summary : null;
    }).filter(Boolean);
    var obsBlock = renderList('Observed in the scanned homepage', observations);
    if (obsBlock) {
      els.reportBody.appendChild(obsBlock);
    } else {
      var none = el('section', 'fes__report-block');
      none.appendChild(el('h4', null, 'Observations'));
      none.appendChild(el('p', null, 'No public-safe observations were produced for this scan.'));
      els.reportBody.appendChild(none);
    }

    if (report.humanReviewNotice && report.humanReviewNotice.summary) {
      var review = el('section', 'fes__report-block fes__report-block--warn');
      review.appendChild(el('h4', null, 'Requires human review'));
      review.appendChild(el('p', null, report.humanReviewNotice.summary));
      els.reportBody.appendChild(review);
    }

    var limitations = report.limitations || {};
    var limitationItems = []
      .concat(limitations.fetchLimitations || [])
      .concat(limitations.renderingLimitations || [])
      .concat(limitations.variantOrMarketLimitations || [])
      .concat(limitations.authorityLimitations || []);
    var limitBlock = renderList('Limitations of this automated scan', limitationItems, { dim: true });
    if (limitBlock) els.reportBody.appendChild(limitBlock);

    var cannot = renderList('This scan cannot establish', limitations.cannotEstablish || [], { dim: true });
    if (cannot) els.reportBody.appendChild(cannot);

    var method = report.methodExplanation || {};
    var methodBlock = el('section', 'fes__report-block');
    methodBlock.appendChild(el('h4', null, 'The E.V.I.L. method'));
    var methodList = el('ul', 'fes__method');
    [['Expose', method.expose, true], ['Validate', method.validate, false],
     ['Implement', method.implement, false], ['Lock In', method.lockIn, false]
    ].forEach(function (entry) {
      var item = el('li');
      if (entry[2]) item.setAttribute('data-current', '');
      var strong = el('strong', null, entry[0] + (entry[2] ? ' (this scan)' : ''));
      item.appendChild(strong);
      if (entry[1]) item.appendChild(document.createTextNode(' — ' + entry[1]));
      methodList.appendChild(item);
    });
    methodBlock.appendChild(methodList);
    if (method.note) methodBlock.appendChild(el('p', null, method.note));
    els.reportBody.appendChild(methodBlock);

    // Deliberate override: the Shopify section setting supplies the initial and fallback CTA
    // destination, and this first-party API field is authoritative for the report state so the
    // backend can route the next step without a theme deploy. No other response field is
    // allowed to change page destinations.
    if (payload.schemaHealthReviewUrl && els.ctaLink) {
      els.ctaLink.href = payload.schemaHealthReviewUrl;
    }
    els.cta.hidden = false;
    showPanel('report');
    announce('Your Free E.V.I.L. Scan report is ready.');
    els.reportTitle.setAttribute('tabindex', '-1');
    els.reportTitle.focus();
    recordCta('displayed');
  }

  function recordCta(eventType) {
    if (eventType === 'displayed' && ctaRecorded) return;
    if (eventType === 'displayed') ctaRecorded = true;
    var token = getToken(STORAGE_ACCESS) || getToken(STORAGE_STATUS);
    if (!token) return;
    api('/api/public/free-scan/cta', {
      method: 'POST',
      token: token,
      body: { eventType: eventType }
    }).catch(function () { /* analytics must never break the page */ });
  }

  if (els.ctaLink) {
    els.ctaLink.addEventListener('click', function () { recordCta('clicked'); });
  }
  if (els.noticeCta) {
    els.noticeCta.addEventListener('click', function () { recordCta('clicked'); });
  }

  /* ---------------- status polling ---------------- */

  function handleStatus(payload) {
    var state = payload.state;
    var ref = payload.publicScanReference;
    switch (state) {
      case 'awaiting_email_verification':
        showProgress('awaiting_email_verification', ref);
        break;
      case 'verification_expired':
        showVerificationExpired(Boolean(payload.resendAvailable));
        break;
      case 'queued':
      case 'fetching':
      case 'scanning':
      case 'filtering':
        showProgress(state, ref);
        schedulePoll();
        break;
      case 'completed':
      case 'completed_with_limitations':
        fetchReport();
        break;
      case 'failed':
        if (payload.reportAvailable) { fetchReport(); } else { showFailed(false); schedulePoll(); }
        break;
      case 'retry_unavailable':
        if (payload.reportAvailable) { fetchReport(); } else { showFailed(true); }
        break;
      case 'recently_scanned':
        showRecentlyScanned();
        break;
      default:
        showNotice('This request is no longer available', [
          'We could not find an active scan for this browser session. You can start a new request.'
        ], { startOver: true });
    }
  }

  function pollStatus() {
    var token = getToken(STORAGE_ACCESS) || getToken(STORAGE_STATUS);
    if (!token) return;
    api('/api/public/free-scan/status', { token: token }).then(function (result) {
      if (result.status === 200) {
        handleStatus(result.data);
      } else if (result.status === 401) {
        var errorState = result.data && result.data.error && result.data.error.state;
        if (errorState === 'result_expired') { showResultExpired(); }
        else { clearTokens(); showPanel('form'); }
      } else if (result.status === 429) {
        schedulePoll(POLL_MS * 3);
      } else {
        schedulePoll(POLL_MS * 2);
      }
    }).catch(function () { schedulePoll(POLL_MS * 2); });
  }

  function schedulePoll(delay) {
    stopPolling();
    pollTimer = window.setTimeout(pollStatus, delay || POLL_MS);
  }

  function fetchReport() {
    var token = getToken(STORAGE_ACCESS);
    if (!token) { showResultExpired(); return; }
    api('/api/public/free-scan/report', { token: token }).then(function (result) {
      if (result.status === 200) {
        renderReport(result.data);
      } else if (result.status === 401) {
        showResultExpired();
      } else if (result.status === 409) {
        schedulePoll();
      } else {
        showFailed(false);
      }
    }).catch(function () { schedulePoll(POLL_MS * 2); });
  }

  /* ---------------- verification exchange ---------------- */

  function extractVerificationToken() {
    var params = new URLSearchParams(window.location.search);
    var token = params.get('fsv');
    if (token) {
      params.delete('fsv');
      var clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
      window.history.replaceState({}, document.title, clean);
    }
    return token || '';
  }

  function exchangeVerification(token) {
    addNoindex();
    showProgress('verifying');
    api('/api/public/free-scan/verifications/exchange', {
      method: 'POST',
      body: { verificationToken: token }
    }).then(function (result) {
      if (result.status === 200) {
        setToken(STORAGE_ACCESS, result.data.accessToken);
        showProgress('verified', result.data.publicScanReference);
        announce('Email confirmed. Your scan is queued.');
        schedulePoll(1500);
      } else if (result.status === 410) {
        showVerificationExpired(false);
      } else if (result.status === 429) {
        showNotice('Too many attempts', [
          'Please wait a few minutes and open the verification link again.'
        ], {});
      } else {
        showNotice('This verification link is not valid', [
          'The link may have already been used or replaced by a newer email. ' +
          'Use the most recent verification email, or submit the form again.'
        ], { startOver: true });
      }
    }).catch(function () {
      showNotice('Connection problem', [
        'We could not reach the scan service. Please check your connection and reload this page.'
      ], {});
    });
  }

  /* ---------------- resend ---------------- */

  /**
   * Single resend implementation shared by the progress and notice panels.
   * The status token is read from sessionStorage and sent only in the request body — it is
   * never written to the DOM, the URL, or analytics.
   */
  function performResend(button, statusEl) {
    if (!button || button.disabled) return; // guards against duplicate clicks while pending
    var token = getToken(STORAGE_STATUS);
    if (!token) {
      var missing = 'This request is no longer active. Please start a new request.';
      setStatus(statusEl, missing, true);
      announce(missing);
      return;
    }
    button.disabled = true;
    setStatus(statusEl, 'Sending a new verification email…', false);
    announce('Sending a new verification email.');
    api('/api/public/free-scan/verifications/resend', {
      method: 'POST',
      body: { statusToken: token }
    }).then(function (result) {
      button.disabled = false;
      if (result.status === 200) {
        setStatus(statusEl, '', false);
        announce('A new verification email is on its way.');
        showProgress('awaiting_email_verification');
      } else if (result.status === 429) {
        var wait = 'Please wait a few minutes before requesting another email.';
        setStatus(statusEl, wait, true);
        announce(wait);
      } else {
        var failed = 'The email could not be resent right now. Please try again shortly.';
        setStatus(statusEl, failed, true);
        announce(failed);
      }
    }).catch(function () {
      button.disabled = false;
      var offline = 'We could not reach the scan service. Check your connection and try again.';
      setStatus(statusEl, offline, true);
      announce(offline);
    });
  }

  if (els.resend) {
    els.resend.addEventListener('click', function () {
      performResend(els.resend, els.resendStatus);
    });
  }
  if (els.noticeResend) {
    els.noticeResend.addEventListener('click', function () {
      performResend(els.noticeResend, els.noticeResendStatus);
    });
  }

  if (els.startOver) {
    els.startOver.addEventListener('click', function () {
      clearTokens();
      stopPolling();
      ctaRecorded = false;
      clearTurnstileError();
      showPanel('form');
      announce('Start a new Free E.V.I.L. Scan request.');
      var first = els.form.querySelector('input, select');
      if (first) first.focus();
      renderTurnstile();
    });
  }

  /* ---------------- form validation + submit ---------------- */

  var FIELD_MESSAGES = {
    businessName: 'Enter your business name.',
    businessEmail: 'Enter a valid business email address.',
    websiteUrl: 'Enter your website URL, e.g. https://www.example.com.',
    relationship: 'Choose your relationship to the business.',
    authorityAttestation: 'Confirm you are authorized to request this scan.',
    consent: 'Accept the terms and privacy policy to continue.'
  };

  function fieldError(input, message) {
    var errorEl = document.getElementById(input.id + '-error');
    if (message) {
      input.setAttribute('aria-invalid', 'true');
      if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
    } else {
      input.removeAttribute('aria-invalid');
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
    }
  }

  function validateForm() {
    var errors = [];
    var fields = {
      businessName: root.querySelector('#fes-business-name'),
      businessEmail: root.querySelector('#fes-business-email'),
      websiteUrl: root.querySelector('#fes-website-url'),
      relationship: root.querySelector('#fes-relationship'),
      authorityAttestation: root.querySelector('#fes-authority'),
      consent: root.querySelector('#fes-consent')
    };
    Object.keys(fields).forEach(function (name) {
      var input = fields[name];
      var valid;
      if (input.type === 'checkbox') valid = input.checked;
      else if (name === 'businessEmail') valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.value.trim());
      else valid = Boolean(input.value.trim());
      fieldError(input, valid ? '' : FIELD_MESSAGES[name]);
      if (!valid) errors.push({ input: input, message: FIELD_MESSAGES[name] });
    });
    return { errors: errors, fields: fields };
  }

  function showErrorSummary(errors) {
    els.errorList.textContent = '';
    errors.forEach(function (entry) {
      var item = el('li');
      var link = el('a', null, entry.message);
      link.href = '#' + entry.input.id;
      link.addEventListener('click', function (event) {
        event.preventDefault();
        entry.input.focus();
      });
      item.appendChild(link);
      els.errorList.appendChild(item);
    });
    els.errorSummary.hidden = false;
    els.errorSummary.focus();
    announce('The form has ' + errors.length + ' problem' + (errors.length === 1 ? '' : 's') + ' to fix.');
  }

  if (els.form) {
    els.form.addEventListener('submit', function (event) {
      event.preventDefault();
      var result = validateForm();
      if (result.errors.length) { showErrorSummary(result.errors); return; }
      els.errorSummary.hidden = true;

      var turnstileToken = '';
      if (window.turnstile && turnstileWidgetId !== null) {
        turnstileToken = window.turnstile.getResponse(turnstileWidgetId) || '';
      }
      if (!turnstileToken) {
        // Sighted users need the visible message; the live region covers screen readers.
        var incomplete = 'Complete the security check before running your scan.';
        showTurnstileError(incomplete);
        announce(incomplete);
        els.turnstileError.scrollIntoView({ block: 'center' });
        els.turnstileError.focus({ preventScroll: true });
        return;
      }
      clearTurnstileError();

      els.submit.disabled = true;
      showProgress('submitting');
      api('/api/public/free-scan/submissions', {
        method: 'POST',
        body: {
          businessName: result.fields.businessName.value.trim(),
          businessEmail: result.fields.businessEmail.value.trim(),
          websiteUrl: result.fields.websiteUrl.value.trim(),
          relationship: result.fields.relationship.value,
          authorityAttestation: result.fields.authorityAttestation.checked,
          termsAccepted: result.fields.consent.checked,
          privacyAccepted: result.fields.consent.checked,
          turnstileToken: turnstileToken
        }
      }).then(function (response) {
        els.submit.disabled = false;
        if (window.turnstile && turnstileWidgetId !== null) {
          window.turnstile.reset(turnstileWidgetId);
        }
        if (response.status === 201) {
          if (response.data.state === 'recently_scanned') {
            showRecentlyScanned();
          } else {
            setToken(STORAGE_STATUS, response.data.statusToken || '');
            showProgress('awaiting_email_verification', response.data.publicScanReference);
            schedulePoll(POLL_MS * 4);
          }
        } else if (response.status === 403) {
          showPanel('form');
          showTurnstileError();
          announce('The security check failed. Please retry it.');
        } else if (response.status === 422) {
          showPanel('form');
          var message = (response.data.error && response.data.error.message) ||
            'The request was not valid. Please review the form.';
          showErrorSummary([{ input: result.fields.businessName, message: message }]);
        } else if (response.status === 429) {
          showNotice('Too many requests', [
            'Please wait a while before requesting another scan.'
          ], {});
        } else {
          showNotice('Something went wrong', [
            'The scan service could not accept the request right now. Please try again shortly.'
          ], { startOver: true });
        }
      }).catch(function () {
        els.submit.disabled = false;
        showPanel('form');
        announce('We could not reach the scan service. Check your connection and try again.');
      });
    });
  }

  /* ---------------- turnstile ---------------- */

  function renderTurnstile() {
    if (!TURNSTILE_SITEKEY || !els.turnstile) {
      if (els.turnstileLoading) els.turnstileLoading.hidden = true;
      return;
    }
    if (!window.turnstile) return;
    if (turnstileWidgetId !== null) {
      window.turnstile.reset(turnstileWidgetId);
      return;
    }
    els.turnstileLoading.hidden = true;
    turnstileWidgetId = window.turnstile.render(els.turnstile, {
      sitekey: TURNSTILE_SITEKEY,
      action: 'free_scan_submit',
      theme: 'dark',
      callback: function () {
        clearTurnstileError();
      },
      'error-callback': function () {
        showTurnstileError();
      }
    });
  }

  function loadTurnstile() {
    if (!TURNSTILE_SITEKEY) {
      if (els.turnstileLoading) els.turnstileLoading.hidden = true;
      return;
    }
    if (window.turnstile) { renderTurnstile(); return; }
    var script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=fesTurnstileReady';
    script.async = true;
    script.defer = true;
    window.fesTurnstileReady = renderTurnstile;
    script.onerror = function () {
      els.turnstileLoading.hidden = true;
      els.turnstileError.hidden = false;
    };
    document.head.appendChild(script);
  }

  /* ---------------- boot ---------------- */

  function boot() {
    if (!API_BASE) {
      showNotice('Scanner not configured', [
        'The Free E.V.I.L. Scan service is not connected yet. Please check back soon.'
      ], {});
      return;
    }
    var verificationToken = extractVerificationToken();
    if (verificationToken) {
      exchangeVerification(verificationToken);
      return;
    }
    if (getToken(STORAGE_ACCESS) || getToken(STORAGE_STATUS)) {
      addNoindex();
      showProgress('eligibility');
      pollStatus();
      loadTurnstile();
      return;
    }
    showPanel('form');
    loadTurnstile();
  }

  boot();
})();
